import { currentIntlLocale } from './i18n';
import { calculatePrecisionOfNumber } from './number';

export type NumberSeparator = '.' | ',';

/**
 * The set of valid number separators that we support for parsing.
 */
const VALID_SEPARATORS = new Set<string>(['.', ',']);

/**
 * Type guard to check if a string is a valid NumberSeparator.
 */
function isValidNumberSeparator(value: string): value is NumberSeparator {
  return VALID_SEPARATORS.has(value);
}

/**
 * Default fallback separators when locale uses unsupported characters.
 * Uses German/European convention (comma for decimal, dot for thousands).
 */
const DEFAULT_LOCALE_INFO: LocaleInfo = {
  decimal: ',',
  thousand: '.',
} as const;

export type LocaleInfo = {
  readonly decimal: NumberSeparator;
  readonly thousand: NumberSeparator;
};

/**
 * Gets locale information for number formatting.
 *
 * Extracts decimal and thousands separators from the locale and validates them
 * against the supported set ('.' and ','). If a locale uses unsupported characters
 * (e.g., narrow no-break space, apostrophe), falls back to safe defaults.
 *
 * @param locale - The locale to get information for (e.g., 'de-DE', 'en-US'). Defaults to the active UI language.
 * @returns An object with validated decimal and thousands separators.
 */
export function getLocaleInfo(
  locale: string = currentIntlLocale(),
): LocaleInfo {
  // Extract raw separators from locale formatting
  const rawThousandsSeparator = Number(1000).toLocaleString(locale).charAt(1);
  const rawDecimalSeparator = Number(1.1).toLocaleString(locale).charAt(1);

  // Validate decimal separator - this is critical for parsing
  const decimal = isValidNumberSeparator(rawDecimalSeparator)
    ? rawDecimalSeparator
    : DEFAULT_LOCALE_INFO.decimal;

  // Validate thousands separator
  // If invalid, use the "opposite" of the decimal separator for safety
  let thousand: NumberSeparator;
  if (isValidNumberSeparator(rawThousandsSeparator)) {
    thousand = rawThousandsSeparator;
  } else {
    // Use the opposite of decimal to avoid conflicts
    thousand = decimal === '.' ? ',' : '.';
  }

  // Ensure decimal and thousand separators are different
  // If they're somehow the same (shouldn't happen with valid locales), use defaults
  if (decimal === thousand) {
    return DEFAULT_LOCALE_INFO;
  }

  return { decimal, thousand };
}

/**
 * Regular expression for localized numbers.
 * Matches optional leading negative sign, initial sequence of one to three digits,
 * optional groups of three digits separated by periods or commas (for thousand separators),
 * and an optional decimal part with one or more digits.
 *
 * Examples:
 * 1234
 * -1234
 * 1,234
 * 1.234
 * 1,234.56
 * 1.234,56
 * 1234.5678
 * -1,234.5678
 */
export const localizedNumberRegex =
  '^-?[0-9]{1,3}(?:[.,][0-9]{3})*(?:[.,][0-9]+)?$';

/**
 * Sanitizes a (potentially dirty) value to a number.
 *
 * @param inputValue - The input value to sanitize.
 * @return The sanitized number.
 */
export const sanitizeNumberValue = (
  inputValue: number | string | undefined | null,
): number | undefined => {
  if (inputValue === undefined || inputValue === null) {
    return undefined;
  }

  if (typeof inputValue === 'string') {
    // Handle empty or whitespace-only strings
    if (inputValue.trim() === '') {
      return undefined;
    }

    // Attempt to parse string to number
    const parsed = Number(inputValue);

    // Check if parsing resulted in valid number
    if (Number.isNaN(parsed)) {
      return undefined;
    }

    return parsed;
  }

  // Handle number inputs
  if (typeof inputValue === 'number') {
    // Check for invalid numbers
    if (Number.isNaN(inputValue) || !Number.isFinite(inputValue)) {
      return undefined;
    }

    return inputValue;
  }

  // Handle unexpected types
  return undefined;
};

type GetFormattedNumberOptions<T = string> = {
  readonly value: number | string | undefined | null;
  readonly digits?: number | 'auto';
  readonly locale?: string;
  readonly fallbackValue?: T;
  readonly forceSign?: boolean;
  /**
   * Digits always shown after the decimal point, padded with zeros; never more
   * than `digits`. Defaults to `digits`, at most 2.
   */
  readonly minimumDigits?: number;
  readonly notation?: Intl.NumberFormatOptions['notation'];
  /**
   * Whether to use grouping (thousand separators). Default is true.
   * Set to false for input fields where users need to edit the number.
   */
  readonly useGrouping?: boolean;
};

type GetFormattedMonetaryAmountOptions<T = string> =
  GetFormattedNumberOptions<T> & {
    readonly currency: string;
  };

/**
 * Formats a number to a string with a given number of digits after the decimal point.
 *
 * @param options - The options object.
 * @param options.value - The value to format.
 * @param options.digits - The number of digits after the decimal point. Use 'auto' to automatically determine the number of digits. Default is 'auto'.
 * @param options.locale - The locale to use for formatting the number. Defaults to the active UI language.
 * @param options.fallbackValue - The value to return if the number is undefined. Default is '—'.
 * @param options.forceSign - Whether to force the display of a plus sign for positive numbers. Default is false.
 * @param options.minimumDigits - Digits always shown after the decimal point. Defaults to `digits`, at most 2.
 * @param options.useGrouping - Whether to use grouping (thousand separators). Default is true.
 * @returns The formatted number.
 *
 * @example
 * getFormattedNumber({ value: 1234.5 })                    // '1.234,5' (German locale)
 * getFormattedNumber({ value: 1234.5, useGrouping: false }) // '1234,5' (for input fields)
 * getFormattedNumber({ value: 5, digits: 3, minimumDigits: 3 }) // '5,000'
 */
export const getFormattedNumber = <T = string>({
  value,
  digits = 'auto',
  locale = currentIntlLocale(),
  fallbackValue = '—' as T,
  forceSign = false,
  minimumDigits,
  notation,
  useGrouping = true,
}: GetFormattedNumberOptions<T>): string | T => {
  const sanitizedValue = sanitizeNumberValue(value);
  const digitsToUse =
    digits === 'auto'
      ? Math.min(calculatePrecisionOfNumber(sanitizedValue), 2)
      : digits;

  if (sanitizedValue === undefined) {
    return fallbackValue;
  }

  const minimumDigitsToUse = Math.min(minimumDigits ?? 2, digitsToUse);

  try {
    // react-doctor-disable-next-line react-doctor/js-hoist-intl -- too marny arguments; hosting yields no improvement
    const formattedValue = new Intl.NumberFormat(locale, {
      maximumFractionDigits: digitsToUse,
      minimumFractionDigits: minimumDigitsToUse,
      notation,
      useGrouping,
    }).format(sanitizedValue);

    // Add plus sign for positive numbers if forceSign is true
    if (forceSign && sanitizedValue > 0) {
      return `+${formattedValue}`;
    }

    return formattedValue;
  } catch (error) {
    // Fallback if Intl.NumberFormat fails (invalid locale, etc.)
    console.warn('Number formatting failed:', error);

    return sanitizedValue?.toString() ?? fallbackValue;
  }
};

/**
 * Formats a number and currency code to a string with a given number of digits after the decimal point and currency symbol.
 *
 * @param options - The options object.
 * @param options.value - The value to format.
 * @param options.currency - The ISO 4217 currency code to use for formatting the number. Default is 'EUR'.
 * @param options.digits - The number of digits after the decimal point. Use 'auto' to automatically determine the number of digits. Default is 'auto'.
 * @param options.locale - The locale to use for formatting the number. Defaults to the active UI language.
 * @param options.fallbackValue - The value to return if the number is undefined. Default is '—'.
 * @param options.forceSign - Whether to force the display of a plus sign for positive numbers. Default is false.
 * @param options.minimumDigits - Digits always shown after the decimal point. Defaults to `digits`, at most 2.
 * @param options.useGrouping - Whether to use grouping (thousand separators). Default is true.
 * @returns The formatted number.
 *
 * @example
 * getFormattedNumber({ value: 1234.5 })                    // '1.234,5' (German locale)
 * getFormattedNumber({ value: 1234.5, useGrouping: false }) // '1234,5' (for input fields)
 */
export const formatMonetaryAmount = <T = string>({
  value,
  currency = 'EUR',
  digits = 'auto',
  locale = currentIntlLocale(),
  fallbackValue = '—' as T,
  forceSign = false,
  minimumDigits,
  notation,
  useGrouping = true,
}: GetFormattedMonetaryAmountOptions<T>): string | T => {
  const sanitizedValue = sanitizeNumberValue(value);
  const digitsToUse =
    digits === 'auto'
      ? Math.min(calculatePrecisionOfNumber(sanitizedValue), 2)
      : digits;

  if (sanitizedValue === undefined) {
    return fallbackValue;
  }

  const minimumDigitsToUse = Math.min(minimumDigits ?? 2, digitsToUse);

  try {
    // A decimal string keeps the sign of a zero ("-0.000000"), and Intl would print it.
    const formattedValue = new Intl.NumberFormat(locale, {
      currency,
      maximumFractionDigits: digitsToUse,
      minimumFractionDigits: minimumDigitsToUse,
      notation,
      style: 'currency',
      useGrouping,
    }).format(sanitizedValue === 0 ? 0 : sanitizedValue);

    // Add plus sign for positive numbers if forceSign is true
    if (forceSign && sanitizedValue > 0) {
      return `+${formattedValue}`;
    }

    return formattedValue;
  } catch (error) {
    // Fallback if Intl.NumberFormat fails (invalid locale, etc.)
    console.warn('Number formatting failed:', error);

    return sanitizedValue?.toString() ?? fallbackValue;
  }
};

/**
 * Formats a number of minutes to a time string in the format 'HH:mm'.
 *
 * @param minutes - The minutes to format.
 * @param locale - (optional) The locale to use for formatting the time. Defaults to the active UI language.
 * @returns The formatted time in the format 'HH:mm'.
 */
export const formatMinutesToTime = (
  minutes: number | string | undefined | null,
  locale: string = currentIntlLocale(),
): string => {
  const sanitizedMinutes = sanitizeNumberValue(minutes);
  if (
    !sanitizedMinutes ||
    Number.isNaN(sanitizedMinutes) ||
    !Number.isFinite(sanitizedMinutes)
  ) {
    return '00:00';
  }

  if (sanitizedMinutes < 0) {
    return '00:00';
  }

  const date = new Date(0, 0, 0, 0, sanitizedMinutes);
  // react-doctor-disable-next-line react-doctor/js-hoist-intl
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

type SeparatorType = 'decimal' | 'locale';

/**
 * Validates thousands grouping pattern (all groups except first must be 3 digits).
 * @param parts - Array of string parts split by separator
 * @returns true if valid thousands grouping, false otherwise
 */
function isValidThousandsGrouping(parts: string[]): boolean {
  for (let index = 1; index < parts.length; index++) {
    const part = parts[index];
    if (part?.length !== 3) {
      return false;
    }
  }

  return true;
}

/**
 * Handles normalization when only one type of separator exists (multiple occurrences).
 * @param normalized - The number string
 * @param separator - The separator character
 * @returns Normalized string or 'NaN' if invalid
 */
function handleMultipleSeparators(
  normalized: string,
  separator: string,
): string {
  const parts = normalized.split(separator);
  if (!isValidThousandsGrouping(parts)) {
    return 'NaN';
  }

  return normalized.replaceAll(separator, '');
}

/**
 * Extracts parts before and after a separator.
 *
 * @param normalized - The normalized number string (whitespace removed, ndash replaced, thousands separators removed, only decimal separator remains)
 * @param separatorIndex - Index of the separator character
 * @returns Object with beforePart and afterPart (excluding negative sign)
 */
function extractSeparatedParts(
  normalized: string,
  separatorIndex: number,
): { beforePart: string; afterPart: string } {
  const afterPart = normalized.slice(separatorIndex + 1);
  const beforePart = normalized.slice(
    normalized.startsWith('-') ? 1 : 0,
    separatorIndex,
  );

  return { beforePart, afterPart };
}

/**
 * Determines whether a separator is a decimal point or thousands separator.
 * Uses heuristics like digit counts and leading zeros to make intelligent decisions.
 *
 * @param beforePart - Substring before the separator
 * @param afterPart - Substring after the separator
 * @returns 'decimal' if decimal separator, 'locale' if ambiguous (uses locale logic)
 */
function determineSeparatorType(
  beforePart: string,
  afterPart: string,
): SeparatorType {
  const digitsBefore = beforePart.length;
  const digitsAfter = afterPart.length;

  // Use locale logic only for the ambiguous case:
  // 1-3 digits before (not just "0") and exactly 3 digits after
  // e.g. 1.000 or 100.001 -> could be thousands in German locale
  if (digitsAfter === 3 && digitsBefore <= 3 && beforePart !== '0') {
    return 'locale';
  }

  // All other cases: treat as decimal separator
  return 'decimal';
}

/**
 * Handles normalization of a single comma.
 * @param normalized - The number string
 * @param lastCommaIndex - Index of the comma
 * @param localeInfo - Validated locale information with guaranteed '.' or ',' separators
 * @returns Normalized string or 'NaN' if invalid
 */
function handleSingleComma(
  normalized: string,
  lastCommaIndex: number,
  localeInfo: LocaleInfo,
): string {
  const { beforePart, afterPart } = extractSeparatedParts(
    normalized,
    lastCommaIndex,
  );

  const separatorType = determineSeparatorType(beforePart, afterPart);

  if (separatorType === 'decimal') {
    return normalized.replace(',', '.');
  }

  // Use locale-specific logic for ambiguous cases
  // localeInfo is guaranteed to contain only '.' or ',' separators
  const thousandsSeparatorRegex = new RegExp(`\\${localeInfo.thousand}`, 'g');
  const decimalSeparatorRegex = new RegExp(`\\${localeInfo.decimal}`, '');

  return normalized
    .replace(thousandsSeparatorRegex, '')
    .replace(decimalSeparatorRegex, '.');
}

/**
 * Handles normalization of a single dot.
 * @param normalized - The number string
 * @param lastDotIndex - Index of the dot
 * @param localeInfo - Validated locale information with guaranteed '.' or ',' separators
 * @returns Normalized string
 */
function handleSingleDot(
  normalized: string,
  lastDotIndex: number,
  localeInfo: LocaleInfo,
): string {
  const { beforePart, afterPart } = extractSeparatedParts(
    normalized,
    lastDotIndex,
  );

  const separatorType = determineSeparatorType(beforePart, afterPart);

  if (separatorType === 'locale') {
    // Use locale-specific logic for ambiguous cases
    // localeInfo is guaranteed to contain only '.' or ',' separators
    const thousandsSeparatorRegex = new RegExp(`\\${localeInfo.thousand}`, 'g');
    const decimalSeparatorRegex = new RegExp(`\\${localeInfo.decimal}`, '');

    return normalized
      .replace(thousandsSeparatorRegex, '')
      .replace(decimalSeparatorRegex, '.');
  }

  // Separator is decimal, dot is already in correct format
  return normalized;
}

type NormalizeStringNumberOptions = {
  /**
   * The locale to use for ambiguous number parsing (e.g., '1.000' could be 1000 in German or 1.0 in English).
   * Defaults to the active UI language.
   */
  readonly locale?: string;
};

/**
 * Normalizes a string number by removing whitespace characters, replacing ndashes with a real minus character,
 * and intelligently handling both European (e.g., 1.234,56) and US (e.g., 1,234.56) number formats.
 *
 * The function uses smart heuristics to determine whether separators are decimal or thousands separators:
 * - If both comma and dot exist, the rightmost one is the decimal separator
 * - For single separators with 3 digits after, the locale is used to disambiguate
 * - For other cases, intelligent defaults are applied
 *
 * @param numberString - The string number to be normalized.
 * @param options - Options including locale for ambiguous parsing. The locale defaults to the active UI language.
 * @returns The normalized string number (using dot as decimal separator), or 'NaN' if invalid.
 *
 * @example
 * normalizeStringNumber('1.234,56')     // '1234.56' (German format)
 * normalizeStringNumber('1,234.56')     // '1234.56' (US format)
 * normalizeStringNumber('1.000', { locale: 'de-DE' })  // '1000' (dot is thousands separator in German)
 * normalizeStringNumber('1.000', { locale: 'en-US' })  // '1' (dot is decimal separator in English)
 */
export function normalizeStringNumber(
  numberString: string,
  options: NormalizeStringNumberOptions = {},
): string {
  const { locale = currentIntlLocale() } = options;

  if (!numberString) {
    return numberString;
  }

  const localeInfo = getLocaleInfo(locale);

  let normalized = numberString
    .replaceAll(/\s/g, '') // Remove all whitespace characters.
    .replace('–', '-'); // Replace ndashes and similar looking characters with a real minus character.

  // Handle leading decimal separator: .5 or ,5 → 0.5
  const hasNegative = normalized.startsWith('-');
  const withoutNegative = hasNegative ? normalized.slice(1) : normalized;
  if (withoutNegative.startsWith('.') || withoutNegative.startsWith(',')) {
    normalized = hasNegative ? '-0' + withoutNegative : '0' + withoutNegative;
  }

  // Detect invalid formats (consecutive separators, improper patterns)
  if (/[.,]{2,}/.test(normalized)) {
    // Consecutive separators like .. or ,, or ., are invalid
    return 'NaN';
  }

  const lastCommaIndex = normalized.lastIndexOf(',');
  const lastDotIndex = normalized.lastIndexOf('.');
  const commaCount = (normalized.match(/,/g) ?? []).length;
  const dotCount = (normalized.match(/\./g) ?? []).length;

  // If both separators exist, the rightmost one is the decimal separator
  if (lastCommaIndex > -1 && lastDotIndex > -1) {
    if (lastCommaIndex > lastDotIndex) {
      // Dot is thousands, comma is decimal: e.g. 1.000,50
      normalized = normalized.replaceAll('.', '').replace(',', '.');
    } else {
      // Comma is thousands, dot is decimal: e.g. 1,000.50
      normalized = normalized.replaceAll(',', '');
    }
  } else if (lastCommaIndex > -1) {
    // Only commas exist
    if (commaCount > 1) {
      normalized = handleMultipleSeparators(normalized, ',');
      if (normalized === 'NaN') {
        return 'NaN';
      }
    } else {
      normalized = handleSingleComma(normalized, lastCommaIndex, localeInfo);
    }
  } else if (lastDotIndex > -1) {
    // Only dots exist
    if (dotCount > 1) {
      normalized = handleMultipleSeparators(normalized, '.');
      if (normalized === 'NaN') {
        return 'NaN';
      }
    } else {
      normalized = handleSingleDot(normalized, lastDotIndex, localeInfo);
    }
  }

  return normalized;
}

type ParseNumberOptions = {
  /**
   * The locale to use for ambiguous number parsing.
   * Defaults to the active UI language.
   */
  readonly locale?: string;
};

/**
 * Parses a localized string or number into a number.
 * Handles both European (e.g., 1.234,56) and US (e.g., 1,234.56) number formats.
 *
 * @param value - The input value to be parsed.
 * @param options - Options including locale for parsing. The locale defaults to the active UI language.
 * @returns The parsed number, or NaN if the input is not a valid number.
 *
 * @example
 * parseNumber('1.234,56')     // 1234.56
 * parseNumber('1,234.56')     // 1234.56
 * parseNumber(123.45)         // 123.45
 * parseNumber('')             // NaN
 */
export function parseNumber(
  value?: string | number,
  options: ParseNumberOptions = {},
): number {
  if (typeof value === 'string') {
    const normalized = normalizeStringNumber(value, options);
    return Number.parseFloat(normalized);
  }

  return value ?? Number.NaN;
}

/**
 * Calculates the number of digits after the decimal point in a given number.
 *
 * @param value - The input number to calculate the digits from.
 * @returns The number of digits after the decimal point.
 *
 * @example
 * numberOfDigits(1.23)    // 2
 * numberOfDigits(1)       // 0
 * numberOfDigits(1.234)   // 3
 */
export function numberOfDigits(value: number): number {
  const digits = String(value).split('.')[1] ?? '';

  return digits.length;
}

/**
 * Formats a number into a string without using scientific notation.
 *
 * @param value - The number to be formatted.
 * @param locale - The locale to use for formatting. Defaults to the active UI language.
 * @returns The formatted number as a string.
 *
 * @example
 * numberToFullWideString(1e-7)  // '0,0000001' (in German locale)
 */
export function numberToFullWideString(
  value: number,
  locale: string = currentIntlLocale(),
): string {
  return Number(value).toLocaleString([locale, 'fullwide'], {
    maximumFractionDigits: 20, // Ensure that the number is not truncated.
    useGrouping: false, // Prevent grouping digits with a thousands separator.
  });
}

type ParseFromInputOptions = {
  /**
   * The locale to use for parsing. Defaults to the active UI language.
   */
  readonly locale?: string;
};

/**
 * Parses a localized user input string to a number.
 * Handles both German (comma decimal) and English (dot decimal) formats.
 *
 * @param input - The user-entered string. A number is returned as is; null and
 * undefined count as empty, so API fields that may be absent can be passed
 * directly.
 * @param options - Parsing options including locale.
 * @returns The parsed number, or null if empty/invalid.
 *
 * @example
 * parseFromInput('1234,5', { locale: 'de-DE' })  // 1234.5
 * parseFromInput('1234.5', { locale: 'en-US' })  // 1234.5
 * parseFromInput('')                              // null
 */
export function parseFromInput(
  input: string | number | null | undefined,
  options: ParseFromInputOptions = {},
): number | null {
  const { locale = currentIntlLocale() } = options;

  if (typeof input === 'number') {
    return input;
  }

  if (!input || input.trim() === '') {
    return null;
  }

  const normalized = normalizeStringNumber(input, { locale });

  if (normalized === 'NaN' || normalized === '') {
    return null;
  }

  const numericValue = Number(normalized);

  return Number.isNaN(numericValue) ? null : numericValue;
}
